import React, { useCallback, useEffect, useState } from "react";
import { Coins, Loader2, RefreshCw, RotateCcw, TrendingDown, Wallet } from "lucide-react";
import {
  CreditSourceTypeLabels,
  CreditTransactionQuery,
  CreditTransactionRow,
  CreditTransactionType,
  CreditTransactionTypeLabels,
  formatCreditAmount,
  getLabelsByLocale,
  Locale,
  PaginatedResponse,
  PaginationQuery,
  PaginationResult,
  UserCreditAccountResponse,
} from "@packages/global";
import { EmptyState, PageHeader, Pagination } from "@packages/ui";
import { paymentsApi } from "../../services/user";
import { useDialog } from "../../hooks/useDialog";

const PAGE_SIZE = 10;

const CreditsPage: React.FC = () => {
  const { alert } = useDialog();
  const [account, setAccount] = useState<UserCreditAccountResponse | null>(null);
  const [transactions, setTransactions] = useState<CreditTransactionRow[]>([]);
  const [pagination, setPagination] = useState<PaginationResult>(emptyPagination());
  const [loading, setLoading] = useState(true);

  const loadAccountAndTransactions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const queryData: PaginationQuery<CreditTransactionQuery> = {
        query: { type: CreditTransactionType.CONSUME },
        pagination: { page, limit: PAGE_SIZE, sortBy: "createdAt", sortOrder: "desc" },
      };
      const [accountData, transactionData] = await Promise.all([
        paymentsApi.creditAccount(),
        paymentsApi.creditTransactions(queryData),
      ]);
      setAccount(accountData);
      setTransactions(transactionData.items);
      setPagination(transactionData.pagination);
    } catch (error: any) {
      await alert(error?.response?.data?.message ?? "积分信息加载失败", "加载失败", "danger");
    } finally {
      setLoading(false);
    }
  }, [alert]);

  useEffect(() => {
    void loadAccountAndTransactions(1);
  }, [loadAccountAndTransactions]);

  const refresh = async () => {
    await loadAccountAndTransactions(pagination.page);
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        eyebrow="Credits"
        title="我的积分"
        description="查看积分余额、累计使用情况和消费历史。"
        actionsSlot={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <button
              type="button"
              onClick={() => void refresh()}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-semibold text-text-secondary hover:bg-bg-elevated"
            >
              <RefreshCw className="h-4 w-4" />
              刷新
            </button>
          </div>
        }
      />

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatTile icon={Wallet} label="当前余额" value={formatCreditAmount(account?.balance ?? 0)} loading={loading} />
        <StatTile icon={Coins} label="累计充值" value={formatCreditAmount(account?.totalRecharged ?? 0)} loading={loading} tone="success" />
        <StatTile icon={TrendingDown} label="累计消费" value={formatCreditAmount(account?.totalConsumed ?? 0)} loading={loading} tone="warning" />
        <StatTile icon={RotateCcw} label="累计退回" value={formatCreditAmount(account?.totalRefunded ?? 0)} loading={loading} tone="info" />
      </section>

      <section className="overflow-hidden rounded-[12px] border border-border-subtle bg-bg-surface">
        <div className="border-b border-border-subtle px-4 py-4 sm:px-6">
          <h2 className="text-base font-semibold text-text-primary">积分消费历史</h2>
          <p className="mt-1 text-xs text-text-muted">按时间倒序展示模型、图片、检索等能力的积分扣费记录。</p>
        </div>
        <div className="p-4 sm:p-6">
          {loading ? (
            <LoadingBox label="加载消费历史中..." />
          ) : transactions.length === 0 ? (
            <EmptyState title="暂无消费记录" description="调用模型或其他能力后，积分消费会出现在这里。" />
          ) : (
            <TransactionsTable
              transactions={transactions}
              pagination={pagination}
              onPageChange={(page) => void loadAccountAndTransactions(page)}
            />
          )}
        </div>
      </section>
    </div>
  );
};

const StatTile: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  loading: boolean;
  tone?: "success" | "warning" | "info";
}> = ({ icon: Icon, label, value, loading, tone }) => {
  const toneClass = {
    success: "text-success",
    warning: "text-warning",
    info: "text-info",
  }[tone ?? "info"];

  return (
    <article className="rounded-[12px] border border-border-subtle bg-bg-surface p-5">
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-[10px] bg-primary-soft text-primary-ink">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[12px] text-text-muted">{label}</div>
          <div className={`mt-1 font-mono text-[24px] font-semibold ${toneClass}`}>
            {loading ? <Loader2 className="h-5 w-5 animate-spin text-text-muted" /> : value}
          </div>
        </div>
      </div>
    </article>
  );
};

const TransactionsTable: React.FC<{
  transactions: CreditTransactionRow[];
  pagination: PaginationResult;
  onPageChange: (page: number) => void;
}> = ({ transactions, pagination, onPageChange }) => {
  const typeLabels = getLabelsByLocale(CreditTransactionTypeLabels, Locale.ZH_CN);
  const sourceLabels = getLabelsByLocale(CreditSourceTypeLabels, Locale.ZH_CN);

  return (
    <div className="overflow-hidden rounded-[12px] border border-border-subtle bg-bg-surface">
      <div className="overflow-x-auto">
        <div className="min-w-[820px]">
          <div className="grid grid-cols-[150px_96px_120px_120px_minmax(220px,1fr)] border-b border-border-subtle bg-bg-elevated font-mono text-[10.5px] text-text-muted">
            <div className="px-4 py-2.5">时间</div>
            <div className="px-4 py-2.5">类型</div>
            <div className="px-4 py-2.5">来源</div>
            <div className="px-4 py-2.5">余额变化</div>
            <div className="px-4 py-2.5">说明</div>
          </div>
          {transactions.map((transaction, index) => (
            <div
              key={transaction.id}
              className={`grid grid-cols-[150px_96px_120px_120px_minmax(220px,1fr)] items-center bg-bg-surface ${
                index > 0 ? "border-t border-border-subtle" : ""
              }`}
            >
              <div className="px-4 py-2.5 font-mono text-[11px] text-text-muted">{formatDateTime(transaction.createdAt)}</div>
              <div className="px-4 py-2.5">
                <span className="inline-flex rounded-full bg-warning-soft px-2 py-[3px] text-[11px] font-semibold text-warning">
                  {typeLabels[transaction.type]}
                </span>
              </div>
              <div className="px-4 py-2.5 text-[12px] text-text-secondary">{sourceLabels[transaction.sourceType]}</div>
              <div className="px-4 py-2.5 font-mono text-[12px] text-text-secondary">
                {formatCreditAmount(transaction.balanceBefore)} → {formatCreditAmount(transaction.balanceAfter)}
              </div>
              <div className="truncate px-4 py-2.5 text-[12px] text-text-secondary">{transaction.description}</div>
            </div>
          ))}
        </div>
      </div>
      <Pagination
        page={pagination.page}
        limit={pagination.limit}
        total={pagination.total}
        totalPages={pagination.totalPages}
        onPageChange={onPageChange}
      />
    </div>
  );
};

function LoadingBox({ label }: { label: string }) {
  return (
    <div className="rounded-[12px] border border-border-subtle bg-bg-surface py-16 text-center text-sm text-text-muted">
      <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin opacity-50" />
      {label}
    </div>
  );
}

function emptyPagination(): PaginationResult {
  return {
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0,
    sortBy: "createdAt",
    sortOrder: "desc",
  };
}

function formatDateTime(value: string | Date) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

export default CreditsPage;
